import { type FC, useMemo, useState } from "react";
import { Pencil, Check, Eye, EyeOff } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { CATEGORY_BG, MODULE_IMAGES } from "@/shared/constants/estateEssentials";
import { cn } from "@/shared/lib/utils";
import { usePerson } from "@/shared/hooks/useAppContext";
import { useHasDeathCert } from "@/shared/hooks/useHasDeathCert";
import { DeathCertModal } from "@/pages/onboarding/components/screens/DeathCertModal";

import type { EssentialModule, FormField, FormFieldTooltip } from "@/shared/types/estateEssentials";

import { TPAModal } from "@/pages/onboarding/components/screens/TPAModal";

import { getStateName } from "@/pages/postloss/estate-essentials/data/stateContent";

import { getDisplayValue, getRevealedDisplayValue } from "../utils/formDisplayUtils";
import { useInlineFormState } from "../hooks/useInlineFormState";
import { DoNotMailForm } from "./DoNotMailForm";
import { FieldTooltip } from "./FieldGrid";
import { SpecialtyReportsForm } from "./SpecialtyReportsForm";
import { InlineFormSidebar } from "./InlineFormSidebar";
import { SubmittedCard } from "./SubmittedCard";
import { Callout } from "@/shared/components/ui/callout";

interface ReviewDisplayItem {
  key: string;
  label: string;
  value: string;
  isSsn: boolean;
  fieldKey?: string;
  tooltip?: FormFieldTooltip;
}

function buildReviewItems(
  fields: FormField[],
  formValues: Record<string, string>,
  deceasedFirstName: string,
): ReviewDisplayItem[] {
  const items: ReviewDisplayItem[] = [];
  const processedCombineKeys = new Set<string>();

  for (const field of fields) {
    if (field.reviewCombine) {
      if (processedCombineKeys.has(field.reviewCombine)) continue;
      processedCombineKeys.add(field.reviewCombine);

      const groupFields = fields.filter((f) => f.reviewCombine === field.reviewCombine);
      const labelSource = groupFields.find((f) => f.reviewLabel);
      let label = labelSource?.reviewLabel ?? field.label;
      if (label === "mailing_address") {
        label = "Current mailing address";
      } else if (label === "address" && deceasedFirstName) {
        label = `${deceasedFirstName}'s address`;
      }

      const isNameCombine = field.reviewCombine.includes("name");
      const separator = isNameCombine ? " " : ", ";
      const parts = groupFields
        .map((f) => {
          const val = formValues[f.key]?.trim() ?? "";
          return getDisplayValue(f, val);
        })
        .filter((v) => v && v !== "-");

      items.push({
        key: field.reviewCombine,
        label,
        value: parts.length > 0 ? parts.join(separator) : "-",
        isSsn: false,
      });
    } else {
      let label = field.label;
      if (field.key === "relationship_to_deceased" && deceasedFirstName) {
        label = `I am ${deceasedFirstName}'s...`;
      }
      items.push({
        key: field.key,
        label,
        value: getDisplayValue(field, formValues[field.key] ?? ""),
        isSsn: field.type === "ssn",
        fieldKey: field.key,
        tooltip: field.tooltip,
      });
    }
  }

  return items;
}

const DEATH_CERT_REQUIRED_TASKS = new Set([
  "do_not_mail",
  "life_insurance_search",
  "notify_credit_bureaus",
]);

interface InlineFormPageProps {
  module: EssentialModule;
}

export const InlineFormPage: FC<InlineFormPageProps> = ({ module }) => {
  const person = usePerson();
  const estateId = person?.estate_id;
  const { hasDeathCert, setHasDeathCert } = useHasDeathCert(estateId);
  const requiresDeathCert = !!module.taskId && DEATH_CERT_REQUIRED_TASKS.has(module.taskId);
  const [deathCertModalOpen, setDeathCertModalOpen] = useState(false);

  const {
    viewState,
    setViewState,
    fields,
    formValues,
    errors,
    ssnVisible,
    setSSNVisible,
    isSubmitting,
    showTPA,
    setShowTPA,
    missingRequiredFields,
    deceasedFirstName,
    handleFieldChange,
    handleSaveAndReview,
    handleSubmitClick,
    performSubmit,
  } = useInlineFormState(module);

  const hasReviewCombine = fields.some((f) => f.reviewCombine);

  const reviewItems = useMemo(
    () =>
      buildReviewItems(
        fields.filter((f) => f.section === "review"),
        formValues,
        deceasedFirstName,
      ),
    [fields, formValues, deceasedFirstName],
  );

  const editableItems = useMemo(
    () =>
      buildReviewItems(
        fields.filter((f) => f.section === "editable"),
        formValues,
        deceasedFirstName,
      ),
    [fields, formValues, deceasedFirstName],
  );

  if (viewState === null) return null;

  const illustration = MODULE_IMAGES[module.id];
  const heroBg = CATEGORY_BG[module.category];

  return (
    <div className="flex gap-8 w-full">
      <div className="flex-1 min-w-0">
        <div className="rounded-lg border border-primary bg-white overflow-hidden">
          {illustration && (
            <div className={cn("h-[180px] py-4 flex items-center justify-center", heroBg)}>
              <img src={illustration} className="h-full" alt="Guide illustration" />
            </div>
          )}

          <div className="px-4 pt-6 pb-4 sm:px-8 sm:pt-8 sm:pb-6">
            <h2 className="mb-2">
              {module.nameTemplate && deceasedFirstName
                ? module.nameTemplate.replace("{{ firstName }}", deceasedFirstName)
                : module.name}
            </h2>
            {(module.formDescription ?? module.description) && (
              <p className="text-secondary">
                {(module.formDescription ?? module.description ?? "").replace(
                  /\{\{\s*deceased_first_name\s*\}\}/g,
                  deceasedFirstName || "your loved one",
                )}
              </p>
            )}
          </div>

          <div className="px-4 pb-6 sm:px-8 sm:pb-8">
            {viewState === "submitted" ? (
              <SubmittedCard
                summary={module.submittedSummary ?? "Your request has been submitted."}
                note={module.submittedNote}
                timeline={module.submittedTimeline ?? []}
                deceasedFirstName={deceasedFirstName}
                templateValues={{
                  deceased_state: getStateName(formValues.deceased_state ?? ""),
                }}
                showViewRequests
              />
            ) : (
              <>
                {viewState === "reviewing" ? (
                  <div className="rounded-lg border border-primary bg-white p-6">
                    <div className="flex items-center justify-between pb-6 mb-6 border-b border-primary">
                      <p className="text-lg font-medium">Review the details below</p>
                      <Button variant="outline" onClick={() => setViewState("editing")}>
                        <Pencil className="h-3.5 w-3.5" />
                        Edit
                      </Button>
                    </div>

                    <div>
                      <p className="text-base font-medium text-primary mb-3">
                        {deceasedFirstName
                          ? `${deceasedFirstName}'s information`
                          : "Deceased's information"}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                        {(hasReviewCombine
                          ? reviewItems
                          : fields.filter((f) => f.section === "review")
                        ).map((item) => {
                          const isItem = "isSsn" in item;
                          const displayItem = isItem ? (item as ReviewDisplayItem) : null;
                          const field = isItem ? null : (item as FormField);
                          const key = displayItem?.key ?? field!.key;
                          const label = displayItem?.label ?? field!.label;
                          const tooltip = displayItem ? displayItem.tooltip : field!.tooltip;
                          const isSsn = displayItem ? displayItem.isSsn : field!.type === "ssn";
                          const value = displayItem
                            ? displayItem.value
                            : getDisplayValue(field!, formValues[field!.key] ?? "");
                          const ssnFieldKey = displayItem?.fieldKey ?? field?.key;

                          return (
                            <div key={key} className="space-y-1">
                              <p className="flex items-center gap-1 text-sm text-secondary">
                                {label}
                                {tooltip && <FieldTooltip tooltip={tooltip} />}
                              </p>
                              {isSsn && ssnFieldKey ? (
                                <div className="flex items-center gap-2">
                                  <p className="leading-relaxed">
                                    {ssnVisible[ssnFieldKey]
                                      ? field
                                        ? getRevealedDisplayValue(
                                            field,
                                            formValues[ssnFieldKey] ?? "",
                                          )
                                        : getRevealedDisplayValue(
                                            {
                                              key: ssnFieldKey,
                                              label: "",
                                              type: "ssn",
                                              required: false,
                                            },
                                            formValues[ssnFieldKey] ?? "",
                                          )
                                      : value}
                                  </p>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setSSNVisible((prev) => ({
                                        ...prev,
                                        [ssnFieldKey]: !prev[ssnFieldKey],
                                      }))
                                    }
                                    className="text-secondary hover:text-primary"
                                  >
                                    {ssnVisible[ssnFieldKey] ? (
                                      <EyeOff className="size-4" />
                                    ) : (
                                      <Eye className="size-4" />
                                    )}
                                  </button>
                                </div>
                              ) : (
                                <p className="leading-relaxed">{value}</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="border-t border-primary mt-6 pt-6">
                      <p className="text-base font-medium text-primary mb-3">Your information</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                        {(hasReviewCombine
                          ? editableItems
                          : fields.filter((f) => f.section === "editable")
                        ).map((item) => {
                          const isItem = "isSsn" in item;
                          const displayItem = isItem ? (item as ReviewDisplayItem) : null;
                          const field = isItem ? null : (item as FormField);
                          const key = displayItem?.key ?? field!.key;
                          const label = displayItem?.label ?? field!.label;
                          const tooltip = displayItem ? displayItem.tooltip : field!.tooltip;
                          const value = displayItem
                            ? displayItem.value
                            : getDisplayValue(field!, formValues[field!.key] ?? "");

                          return (
                            <div key={key} className="space-y-1">
                              <p className="flex items-center gap-1 text-sm text-secondary">
                                {label}
                                {tooltip && <FieldTooltip tooltip={tooltip} />}
                              </p>
                              <p className="leading-relaxed">{value}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-primary bg-white p-6">
                    <div className="flex items-center justify-between pb-6 mb-6 border-b border-primary">
                      <p className="text-lg font-medium">Review the details below</p>
                      <Button variant="outline" onClick={handleSaveAndReview}>
                        <Check className="h-3.5 w-3.5" />
                        Done
                      </Button>
                    </div>

                    {missingRequiredFields.length > 0 && (
                      <Callout
                        variant="warning"
                        className="mb-6"
                        title={`${missingRequiredFields.length} field${
                          missingRequiredFields.length > 1 ? "s" : ""
                        } need${
                          missingRequiredFields.length === 1 ? "s" : ""
                        } your attention: highlighted below`}
                      />
                    )}

                    {module.id === "do-not-mail" ? (
                      <DoNotMailForm
                        fields={fields}
                        values={formValues}
                        errors={errors}
                        onChange={handleFieldChange}
                        deceasedFirstName={deceasedFirstName}
                        estateId={estateId}
                      />
                    ) : (
                      <SpecialtyReportsForm
                        fields={fields}
                        values={formValues}
                        errors={errors}
                        onChange={handleFieldChange}
                        deceasedFirstName={deceasedFirstName}
                        estateId={estateId}
                      />
                    )}
                  </div>
                )}

                {viewState === "editing" && Object.keys(errors).length > 0 && (
                  <p className="text-sm text-amber-600 mt-4">Please fill in all required fields.</p>
                )}

                <Button
                  variant="brand"
                  rounded
                  className="w-full mt-6"
                  onClick={
                    viewState === "editing"
                      ? handleSaveAndReview
                      : requiresDeathCert && !hasDeathCert
                        ? () => setDeathCertModalOpen(true)
                        : handleSubmitClick
                  }
                  disabled={isSubmitting}
                >
                  {viewState === "editing" ? "Review and submit" : "Submit"}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {module.sidebarPanels && module.sidebarPanels.length > 0 && (
        <aside className="hidden lg:block w-[280px] shrink-0 sticky top-24 self-start">
          <InlineFormSidebar panels={module.sidebarPanels} deceasedFirstName={deceasedFirstName} />
        </aside>
      )}

      {requiresDeathCert && estateId && (
        <DeathCertModal
          isOpen={deathCertModalOpen}
          onOpenChange={setDeathCertModalOpen}
          estateId={estateId}
          firstName={deceasedFirstName}
          onUploaded={() => {
            setHasDeathCert();
            setDeathCertModalOpen(false);
            handleSubmitClick();
          }}
          onSkip={() => setDeathCertModalOpen(false)}
        />
      )}

      <TPAModal
        isOpen={showTPA}
        onOpenChange={setShowTPA}
        onSigned={performSubmit}
        onComplete={() => {}}
        answers={{}}
        setAnswer={() => {}}
        goToScreen={() => {}}
      />
    </div>
  );
};
